import { Reducer, useEffect, useReducer } from "react"

import { useAuth } from "./auth"
import { getLibraryImports, getProfile } from "../profile/profile"
import { fetchFollowing, fetchFollowers } from "../profile/folllow"

import { LibraryImport, Profile } from "../profile/data-types"
import { User } from "../auth/data-types"


type ProfileState = {
    pending: boolean
    data?: Profile
    error?: string
}

type LibraryImportsState = {
    pending: boolean
    data?: LibraryImport[]
    error?: string
}

type UserListState = {
    pending: boolean
    data?: User[]
    error?: string
}

export const useProfile = (publicId: string): ProfileState => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<ProfileState, Partial<ProfileState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            data: undefined,
        }
    )

    useEffect(() => {
        const f = async () => {
            setState({ pending: true })
            try {
                const club = await getProfile(publicId, user.token?.access)
                setState({ pending: false, data: club })
            } catch (e) {
                console.log(`Error getting profile: ${e}`)
                setState({ pending: false, error: "Error getting profile"})
            }
        }
        f()
    }, [publicId, user.token])

    return state
}


export const useLibraryImports = (): LibraryImportsState => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<LibraryImportsState, Partial<LibraryImportsState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            data: undefined,
        }
    )

    useEffect(() => {
        const f = async () => {
            setState({ pending: true })
            try {
                const imports = await getLibraryImports(user.token?.access!)
                setState({ pending: false, data: imports })
            } catch (e) {
                console.log(`Error getting imports: ${e}`)
                setState({ pending: false, error: "Error getting imports"})
            }
        }
        f()
    }, [user.token])

    return state
}

export const useFollowing = (targetPublicId: string | null): [UserListState, (offset: number) => void] => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<UserListState, Partial<UserListState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            data: undefined,
        }
    )

    const update = async (offset: number) => {
        setState({ pending: true })
        try {
            const users = await fetchFollowing(targetPublicId, offset, 20, user.token?.access!)
            setState({ pending: false, data: [...(state.data || []), ...users] })
        } catch (e) {
            console.log(`Error fetching following: ${e}`)
            setState({ pending: false, error: "Error fetching following users "})
        }
    }

    useEffect(() => {
        update(0)
    }, [user.token, targetPublicId])

    return [state, update]
}

export const useFollowers = (targetPublicId: string | null): [UserListState, (offset: number) => void] => {
    const user = useAuth()

    const [state, setState] = useReducer<Reducer<UserListState, Partial<UserListState>>>(
        (state, newState) => ({...state, ...newState}),
        {
            pending: false, 
            data: undefined,
        }
    )

    const update = async (offset: number) => {
        setState({ pending: true })
        try {
            const users = await fetchFollowers(targetPublicId, offset, 20, user.token?.access!)
            setState({ pending: false, data: [...(state.data || []), ...users] })
        } catch (e) {
            console.log(`Error fetching following: ${e}`)
            setState({ pending: false, error: "Error fetching following users "})
        }
    }

    useEffect(() => {
        if ((state.data?.length || 0) > 0) {
            setState({
                pending: false, 
                data: undefined,
            })
        }
        update(0)
    }, [user.token, targetPublicId])

    return [state, update]
}


